import { useEffect, useMemo, useRef } from 'react';

import { isDefined } from '@enterprise/core';

import { useCallbackRef } from '#misc/callback-ref/callback-ref.hook';

/**
 * Provides a debounced version of a callback function, delaying its execution until after a specified delay.
 *
 * Usage:
 *   const debouncedCallback = useDebounceCallback((...args) => { ... }, 500);
 *   debouncedCallback(arg1, arg2); // Will only execute after 500ms of inactivity.
 *
 * This is useful for scenarios like search input, resize events,
 * or any case where you want to limit how often a function is called.
 *
 * @template T extends (...args: never[]) => unknown
 * @param {T} callback - The callback function to debounce.
 * @param {number} [delay=300] - The debounce delay in milliseconds. Defaults to 300ms.
 * @returns {(...args: Parameters<T>) => void} A debounced function that delays invoking the callback.
 *
 * Example:
 *   const handleInput = useDebounceCallback((value) => { ... }, 400);
 *   <input onChange={e => handleInput(e.target.value)} />;
 *
 * The returned function identity is stable and will always use the latest callback and delay values.
 */
export function useDebounceCallback<T extends (...args: never[]) => unknown>(callback: T, delay = 300) {
  const debounceTimerRef = useRef<null | number>(null);
  const handleCallbackRef = useCallbackRef(callback);

  useEffect(() => {
    return () => {
      if (isDefined(debounceTimerRef.current)) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return useMemo(() => {
    const debounced = (...args: Parameters<T>) => {
      if (isDefined(debounceTimerRef.current)) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = window.setTimeout(() => {
        handleCallbackRef(...args);
      }, delay);
    };

    return debounced as T;
  }, [delay, handleCallbackRef]);
}
